import React, { useRef, useState, useEffect } from "react";

import { validateUrl, getImageSize } from "../../utils/utils";
import {
	getCalloutGeometry,
	getCalloutPresentation,
	getCalloutTextRect
} from "../markupImage/mediaMarkupCalloutUtils";

const SPOTLIGHT_EFFECT = "spotlight";
const SPOTLIGHT_DEFAULTS = {
	backgroundColor: "rgba(0,0,0,1)",
	opacity: 0.55,
	blur: 0
};
let imageWithMarkupSpotlightIdCounter = 0;

function getNextImageWithMarkupSpotlightId() {
	imageWithMarkupSpotlightIdCounter += 1;
	return `image-with-markup-spotlight-${imageWithMarkupSpotlightIdCounter}`;
}

function sanitizeSvgId(value = "") {
	return `${value || "item"}`.replace(/[^A-Za-z0-9_-]/g, "-");
}

function clampNumber(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizeSpotlightOpacity(value = SPOTLIGHT_DEFAULTS.opacity) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return SPOTLIGHT_DEFAULTS.opacity;
	}

	return clampNumber(numericValue > 1 ? numericValue / 100 : numericValue, 0, 1);
}

function normalizeSpotlightBlur(value = SPOTLIGHT_DEFAULTS.blur) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return SPOTLIGHT_DEFAULTS.blur;
	}

	return clampNumber(numericValue, 0, 40);
}

function getHotspotEffect(hotspot = {}) {
	if ((hotspot.shape || "").toLowerCase() === "callout") {
		return "normal";
	}

	return hotspot.effect === SPOTLIGHT_EFFECT || (hotspot.spotlight && hotspot.spotlight.enabled === true) ? SPOTLIGHT_EFFECT : "normal";
}

function getSpotlightSettings(hotspot = {}) {
	const spotlight = hotspot.spotlight && typeof hotspot.spotlight === "object" ? hotspot.spotlight : {};
	return {
		backgroundColor: typeof spotlight.backgroundColor === "string" && spotlight.backgroundColor.trim() ? spotlight.backgroundColor : SPOTLIGHT_DEFAULTS.backgroundColor,
		opacity: normalizeSpotlightOpacity(spotlight.opacity !== undefined ? spotlight.opacity : SPOTLIGHT_DEFAULTS.opacity),
		blur: normalizeSpotlightBlur(spotlight.blur !== undefined ? spotlight.blur : SPOTLIGHT_DEFAULTS.blur)
	};
}

function getSpotlightLayerSettings(hotspots = []) {
	if (!Array.isArray(hotspots) || hotspots.length === 0) {
		return SPOTLIGHT_DEFAULTS;
	}

	return hotspots.reduce((result, hotspot) => {
		const setting = getSpotlightSettings(hotspot);
		const itemOpacity = hotspot.opacity !== undefined ? normalizeSpotlightOpacity(hotspot.opacity) : 1;
		return {
			backgroundColor: setting.backgroundColor || result.backgroundColor,
			opacity: Math.max(result.opacity, normalizeSpotlightOpacity(setting.opacity) * itemOpacity),
			blur: Math.max(result.blur, normalizeSpotlightBlur(setting.blur))
		};
	}, Object.assign({}, getSpotlightSettings(hotspots[0]), {
		opacity: 0
	}));
}

function getArrowPoints(hotspot = {}) {
	const coords = hotspot.coords || {};
	return `${coords.x} ${coords.y + (coords.height * 0.33)},${coords.x + (coords.width * 0.5)} ${coords.y + (coords.height * 0.33)},${coords.x + (coords.width * 0.5)} ${coords.y},${coords.x + (coords.width)} ${coords.y + (coords.height * 0.5)},${coords.x + (coords.width * 0.5)} ${coords.y + (coords.height)},${coords.x + (coords.width * 0.5)} ${coords.y + (coords.height * 0.67)},${coords.x} ${coords.y + (coords.height * 0.67)},${coords.x} ${coords.y + (coords.height * 0.33)}`;
}

function getDiamondPoints(hotspot = {}) {
	const coords = hotspot.coords || {};
	const midX = coords.x + (coords.width * 0.5);
	const midY = coords.y + (coords.height * 0.5);
	return `${midX} ${coords.y},${coords.x + coords.width} ${midY},${midX} ${coords.y + coords.height},${coords.x} ${midY}`;
}

function renderMarkupShapeElement(hotspot, props = {}) {
	const shape = hotspot && hotspot.shape ? hotspot.shape.toLowerCase() : "rectangle";
	const coords = hotspot.coords || {};

	if (shape === "arrow") {
		return <polygon {...props} points={getArrowPoints(hotspot)} strokeLinejoin="round" />;
	}
	if (shape === "callout") {
		const presentation = getCalloutPresentation(hotspot);
		const geometry = getCalloutGeometry(
			coords,
			presentation.pointerPosition,
			presentation.pointerSize,
			hotspot.style && hotspot.style.borderRadius
		);
		return <path {...props} d={geometry.path} strokeLinejoin="round" />;
	}
	if (shape === "ellipse") {
		return <ellipse {...props} cx={coords.x + (coords.width * 0.5)} cy={coords.y + (coords.height * 0.5)} rx={Math.abs(coords.width) * 0.5} ry={Math.abs(coords.height) * 0.5} />;
	}
	if (shape === "diamond") {
		return <polygon {...props} points={getDiamondPoints(hotspot)} strokeLinejoin="round" />;
	}
	if (shape === "line") {
		return <line {...props} x1={coords.x} y1={coords.y} x2={coords.x + coords.width} y2={coords.y + coords.height} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
	}
	return <rect {...props} rx={hotspot.style && hotspot.style.borderStyle && hotspot.style.borderRadius ? hotspot.style.borderRadius : 0} x={coords.x} y={coords.y} width={coords.width} height={coords.height} />;
}

function renderMarkupCalloutText(hotspot) {
	const shape = hotspot && hotspot.shape ? hotspot.shape.toLowerCase() : "rectangle";
	if (shape !== "callout") {
		return null;
	}

	const presentation = getCalloutPresentation(hotspot);
	const textRect = getCalloutTextRect(hotspot);
	const verticalAlignment = presentation.verticalAlign === "top"
		? "flex-start"
		: (presentation.verticalAlign === "bottom" ? "flex-end" : "center");

	return (
		<foreignObject x={textRect.x} y={textRect.y} width={textRect.width} height={textRect.height} pointerEvents="none">
			<div
				xmlns="http://www.w3.org/1999/xhtml"
				style={{
					boxSizing: "border-box",
					display: "flex",
					alignItems: verticalAlignment,
					width: "100%",
					height: "100%",
					overflow: "hidden",
					color: presentation.textColor,
					fontFamily: presentation.fontFamily || "Arial, Helvetica, sans-serif",
					fontSize: `${presentation.fontSize}px`,
					fontWeight: presentation.fontWeight,
					lineHeight: presentation.lineHeight > 4 ? `${presentation.lineHeight}px` : presentation.lineHeight,
					textAlign: presentation.textAlign,
					whiteSpace: "pre-wrap",
					overflowWrap: "anywhere",
					wordBreak: "break-word"
				}}
			>
				<div style={{ width: "100%" }}>{presentation.text}</div>
			</div>
		</foreignObject>
	);
}

function renderMarkupHotspot(hotspot, index) {
	const coords = hotspot.coords || {};
	const style = hotspot.style || {};
	const rotation = hotspot.rotation ? Number(hotspot.rotation) || 0 : 0;
	const centerX = coords.x + (coords.width * 0.5);
	const centerY = coords.y + (coords.height * 0.5);
	const rotationTransform = rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined;
	const sharedProps = {
		strokeWidth: Object.prototype.hasOwnProperty.call(style, "borderWidth") ? style.borderWidth : 5,
		stroke: style.borderColor ? style.borderColor : "darkorange",
		fill: style.borderFillColor ? style.borderFillColor : "transparent",
		pointerEvents: "none"
	};

	if (getHotspotEffect(hotspot) !== SPOTLIGHT_EFFECT) {
		return (
			<g key={hotspot.guid || `markup-hotspot-${index}`} transform={rotationTransform}>
				{renderMarkupShapeElement(hotspot, sharedProps)}
				{renderMarkupCalloutText(hotspot)}
			</g>
		);
	}

	const authoredBorderWidth = Number(style.borderWidth) || 0;
	return (
		<g key={hotspot.guid || `markup-spotlight-${index}`}>
			{authoredBorderWidth > 0 && style.borderColor && style.borderColor !== "transparent" && (
				<g transform={rotationTransform} pointerEvents="none">
					{renderMarkupShapeElement(hotspot, { strokeWidth: authoredBorderWidth, stroke: style.borderColor, fill: "transparent" })}
				</g>
			)}
			{renderMarkupCalloutText(hotspot)}
		</g>
	);
}

function renderMarkupSpotlightLayer(hotspots = [], imageSize = {}, url = "", idPrefix = "image-with-markup-spotlight") {
	const spotlightHotspots = hotspots.filter((hotspot) => getHotspotEffect(hotspot) === SPOTLIGHT_EFFECT);
	if (spotlightHotspots.length === 0) {
		return null;
	}

	const imageWidth = Number(imageSize.width) || 0;
	const imageHeight = Number(imageSize.height) || 0;
	if (!imageWidth || !imageHeight) {
		return null;
	}

	const spotlight = getSpotlightLayerSettings(spotlightHotspots);
	const blur = normalizeSpotlightBlur(spotlight.blur);
	const sanitizedIdPrefix = sanitizeSvgId(idPrefix);
	const maskId = `${sanitizedIdPrefix}-mask`;
	const filterId = `${sanitizedIdPrefix}-blur`;

	return (
		<g key={`${sanitizedIdPrefix}-layer`}>
			<defs>
				{blur > 0 && (
					<filter id={filterId} x={-imageWidth * 0.1} y={-imageHeight * 0.1} width={imageWidth * 1.2} height={imageHeight * 1.2} filterUnits="userSpaceOnUse">
						<feGaussianBlur in="SourceGraphic" stdDeviation={blur} />
					</filter>
				)}
				<mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse" x="0" y="0" width={imageWidth} height={imageHeight}>
					<rect x="0" y="0" width={imageWidth} height={imageHeight} fill="#ffffff" />
					{spotlightHotspots.map((hotspot, index) => {
						const coords = hotspot.coords || {};
						const style = hotspot.style || {};
						const shape = hotspot && hotspot.shape ? hotspot.shape.toLowerCase() : "rectangle";
						const rotation = hotspot.rotation ? Number(hotspot.rotation) || 0 : 0;
						const centerX = coords.x + (coords.width * 0.5);
						const centerY = coords.y + (coords.height * 0.5);
						const rotationTransform = rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined;
						const authoredBorderWidth = Number(style.borderWidth) || 0;
						const cutoutStrokeWidth = shape === "line" ? Math.max(authoredBorderWidth, 18) : Math.max(authoredBorderWidth, 1);

						return (
							<g key={`image-with-markup-spotlight-cutout-${hotspot.guid || index}`} transform={rotationTransform}>
								{renderMarkupShapeElement(hotspot, { strokeWidth: cutoutStrokeWidth, stroke: "#000000", fill: shape === "line" ? "none" : "#000000" })}
							</g>
						);
					})}
				</mask>
			</defs>
			<g mask={`url(#${maskId})`} pointerEvents="none">
				{blur > 0 && <image href={url} x="0" y="0" width={imageWidth} height={imageHeight} filter={`url(#${filterId})`} />}
				<rect x="0" y="0" width={imageWidth} height={imageHeight} fill={spotlight.backgroundColor} opacity={spotlight.opacity} />
			</g>
		</g>
	);
}

const ImageWithMarkup = (props) => {
	const svgRef = useRef();
	const spotlightIdPrefixRef = useRef(null);
	if (!spotlightIdPrefixRef.current) {
		spotlightIdPrefixRef.current = getNextImageWithMarkupSpotlightId();
	}

	const [imageSize, setImageSize] = useState(null);

	const { url, imageHotspots } = props;

	useEffect(() => {
		async function asyncGetImageSize() {
			const imageSize = await getImageSize(url);

			setImageSize({
				width: imageSize.width,
				height: imageSize.height
			});
		}

		asyncGetImageSize();

		// Anything in here is fired on component unmount.
		return () => {
		};
	}, [url]);

	const renderComponent = () => {
		// If no url or an image and don't have size yet return
		if (imageSize === null || url === null || url === undefined) {
			return null;
		}

		let hotspots = null;
		let spotlightLayer = null;
		if (imageHotspots && imageHotspots.length > 0) {
			hotspots = imageHotspots.map((hotspot, index) => renderMarkupHotspot(hotspot, index));
			spotlightLayer = renderMarkupSpotlightLayer(imageHotspots, imageSize, url, spotlightIdPrefixRef.current);
		}

		const result = url && validateUrl(url) ? <div style={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
			{/* <img alt="Preview" className="player-image" style={{ flex: 1, maxHeight: "50vh" }} src={url} /> */}
			<svg
				ref={svgRef}
				xmlns="http://www.w3.org/2000/svg"
				viewBox={`0 0 ${imageSize.width} ${imageSize.height}`}
				width="100%"
				style={{
					display: "block",
					width: "100%",
					height: "auto",
					maxWidth: "100%",
					maxHeight: "50vh"
				}}
			>
				<image href={url} />
				{spotlightLayer}
				{hotspots}
			</svg>
		</div> : null;

		return result;
	};

	return renderComponent();
};

export default ImageWithMarkup;
